import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { toast } from 'sonner';
import { sdk } from '@/services/sdk';
import LookupPicker from '@/components/LookupPicker';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Field } from '@/components/kit';
import { FormDialog, PickSelect } from '@/components/inputs';
import { LEDGER_KINDS, LEDGER_KIND_LABELS, ledgerName, num, signedMoney } from '@/shared/ledger';
import { costForCredits, findRate, rateFor, rateLabel, serviceOfOrder, useCreditRates } from '@/shared/creditRates';
import { money, orderTotal } from '@/shared/pricing';
import { committed, idOf, nameOf, todayDate } from '@/shared/data';

const asText = (v) => (v === null || v === undefined ? '' : String(v));

const NO_SERVICE = '__none';

function blank(order) {
  return {
    entry_date_c: todayDate(),
    kind_c: order ? 'Activation' : 'Expense',
    order_c: order?.Id ?? null,
    orderName: order?.Name ?? '',
    service: serviceOfOrder(order),
    costTouched: false,
    credits_c: '',
    cost_c: '',
    collected_c: '',
    notes_c: '',
  };
}

function fromEntry(e, order) {
  return {
    entry_date_c: e.entry_date_c ? String(e.entry_date_c).slice(0, 10) : todayDate(),
    kind_c: e.kind_c || 'Other',
    order_c: idOf(e.order_c) ?? order?.Id ?? null,
    orderName: nameOf(e.order_c) || order?.Name || '',
    service: e.service_c || serviceOfOrder(order),
    costTouched: num(e.cost_c) !== null,
    credits_c: asText(e.credits_c),
    cost_c: asText(e.cost_c),
    collected_c: asText(e.collected_c),
    notes_c: e.notes_c ?? '',
  };
}

/**
 * Add or edit a ledger entry: credits used, what it cost, what was collected.
 * Pass `order` to tie it to one order, `entry` to edit.
 */
export default function LedgerDialog({ open, onOpenChange, entry, order, onSaved, title, description }) {
  const editing = !!entry;
  const [form, setForm] = useState(blank(order));
  const [saving, setSaving] = useState(false);
  const { rates, reload } = useCreditRates();

  useEffect(() => {
    if (!open) return;
    const next = entry ? fromEntry(entry, order) : blank(order);
    setForm(next);
    reload();
    if (next.order_c && !next.service) loadOrder(next.order_c);
  }, [open]);

  useEffect(() => {
    setForm((f) => withAutoCost(f));
  }, [rates]);

  const set = (key) => (value) => setForm((f) => ({ ...f, [key]: value }));

  /** Cost follows credits x rate until it is typed by hand. */
  const withAutoCost = (f) => {
    if (f.costTouched) return f;
    const cost = costForCredits(rates, f.service, f.credits_c);
    return { ...f, cost_c: cost === null ? '' : cost.toFixed(2) };
  };

  function setCredits(value) {
    setForm((f) => withAutoCost({ ...f, credits_c: value }));
  }

  function setService(value) {
    const service = value === NO_SERVICE ? '' : value;
    setForm((f) => withAutoCost({ ...f, service }));
  }

  function setCost(value) {
    setForm((f) => ({ ...f, cost_c: value, costTouched: value !== '' }));
  }

  async function loadOrder(id) {
    const r = await sdk.table('orders_c').select(['Name', 'plan_c', 'product_c']).get(id);
    if (!r?.data) return;
    setForm((f) => {
      if (f.order_c !== id) return f;
      const next = { ...f, orderName: r.data.Name ?? '' };
      if (!f.service) next.service = serviceOfOrder(r.data);
      return withAutoCost(next);
    });
  }

  function pickOrder(id) {
    setForm((f) => withAutoCost({ ...f, order_c: id || null, orderName: '', service: '' }));
    if (id) loadOrder(id);
  }

  const creditsN = num(form.credits_c);
  const costN = num(form.cost_c);
  const collectedN = num(form.collected_c);
  const profit = (collectedN ?? 0) - (costN ?? 0);
  const typed = creditsN !== null || costN !== null || collectedN !== null;

  async function submit() {
    if (!typed) {
      toast.error('Type at least one number: credits, cost or collected');
      return;
    }
    if ([creditsN, costN, collectedN].some((v) => v !== null && v < 0)) {
      toast.error('Numbers cannot be negative');
      return;
    }
    if (!form.order_c && !form.notes_c.trim()) {
      toast.error('Add a note so you know what this entry was for');
      return;
    }
    const fields = {
      Name: ledgerName({ kind: form.kind_c, orderName: form.order_c ? form.orderName : '', notes: form.notes_c }),
      entry_date_c: form.entry_date_c || todayDate(),
      kind_c: form.kind_c,
      order_c: form.order_c || null,
      service_c: form.service || '',
      credits_c: creditsN,
      cost_c: costN,
      collected_c: collectedN,
      notes_c: form.notes_c.trim(),
    };
    setSaving(true);
    try {
      const res = editing
        ? await sdk.table('ledger_c').update({ Id: entry.Id, ...fields })
        : await sdk.table('ledger_c').create(fields);
      const saved = committed(res, editing ? 'Ledger entry saved' : 'Ledger entry added');
      if (!saved) return;
      onOpenChange(false);
      setForm(blank(order));
      onSaved?.(saved);
    } finally {
      setSaving(false);
    }
  }

  const orderHint = order ? `Order total ${money(orderTotal(order))}` : undefined;
  const rate = rateFor(rates, form.service);
  let creditsHint;
  if (rate !== null) creditsHint = rateLabel(rates, form.service);
  else if (form.service) creditsHint = 'No cost per credit saved for this service';
  const serviceNames = rates.map((r) => r.Name).filter(Boolean);
  if (form.service && !findRate(rates, form.service)) serviceNames.push(form.service);
  const serviceValues = [NO_SERVICE, ...serviceNames];
  const serviceLabels = { [NO_SERVICE]: 'No service' };
  const autoCost = rate !== null && !form.costTouched && creditsN !== null;
  const costHint = autoCost ? 'Worked out from credits. Edit if it differs.' : undefined;

  return (
    <FormDialog
      open={open}
      onOpenChange={onOpenChange}
      title={title ?? (editing ? 'Edit ledger entry' : 'Add a ledger entry')}
      description={description ?? 'For your books only. Customers never see this.'}
      onSubmit={submit}
      saving={saving}
      submitLabel={editing ? 'Save entry' : 'Add entry'}
      busyLabel="Saving…"
      wide
    >
      <div className="grid gap-4 sm:grid-cols-2">
        <Field label="Date" htmlFor="ledger-date">
          <Input
            id="ledger-date"
            type="date"
            value={form.entry_date_c}
            onChange={(e) => set('entry_date_c')(e.target.value)}
            placeholder="2026-11-03"
          />
        </Field>
        <Field label="Kind" htmlFor="ledger-kind">
          <PickSelect
            id="ledger-kind"
            value={form.kind_c}
            onChange={set('kind_c')}
            values={LEDGER_KINDS}
            labels={LEDGER_KIND_LABELS}
            placeholder="What it was"
          />
        </Field>
      </div>
      <Field label="Order" htmlFor="ledger-order" hint={order ? undefined : 'Optional. Leave empty for costs not tied to one order.'}>
        <LookupPicker
          table="orders_c"
          searchField="Name"
          value={form.order_c}
          onChange={pickOrder}
          placeholder="Search order codes…"
          clearable={!order}
        />
      </Field>
      <Field
        label="Service"
        htmlFor="ledger-service"
        hint={
          <>
            {'Sets the cost per credit. '}
            <Link to="/admin/ledger/rates" className="underline hover:text-foreground">
              Manage services and rates
            </Link>
          </>
        }
      >
        <PickSelect
          id="ledger-service"
          value={findRate(rates, form.service)?.Name ?? (form.service || NO_SERVICE)}
          onChange={setService}
          values={serviceValues}
          labels={serviceLabels}
          placeholder="Pick a service"
        />
      </Field>
      <div className="grid gap-4 sm:grid-cols-3">
        <Field label="Credits used" htmlFor="ledger-credits" hint={creditsHint}>
          <Input
            id="ledger-credits"
            type="number"
            min={0}
            step="0.01"
            inputMode="decimal"
            value={form.credits_c}
            onChange={(e) => setCredits(e.target.value)}
            placeholder="12"
          />
        </Field>
        <Field label="Cost to you (USD)" htmlFor="ledger-cost" hint={costHint}>
          <Input
            id="ledger-cost"
            type="number"
            min={0}
            step="0.01"
            inputMode="decimal"
            value={form.cost_c}
            onChange={(e) => setCost(e.target.value)}
            placeholder="18.00"
          />
        </Field>
        <Field label="Collected (USD)" htmlFor="ledger-collected" hint={orderHint}>
          <Input
            id="ledger-collected"
            type="number"
            min={0}
            step="0.01"
            inputMode="decimal"
            value={form.collected_c}
            onChange={(e) => set('collected_c')(e.target.value)}
            placeholder="40.00"
          />
        </Field>
      </div>
      {typed && (
        <p className="rounded-md border border-border bg-muted/40 px-3 py-2 text-sm">
          {profit >= 0 ? 'You keep ' : 'You lose '}
          <span className={profit >= 0 ? 'font-semibold text-success' : 'font-semibold text-destructive'}>
            {signedMoney(Math.abs(profit))}
          </span>
          {' on this entry.'}
        </p>
      )}
      <Field label="Notes" htmlFor="ledger-notes">
        <Textarea
          id="ledger-notes"
          rows={3}
          value={form.notes_c}
          onChange={(e) => set('notes_c')(e.target.value)}
          placeholder="Topped up 50 credits, paid by Wise…"
        />
      </Field>
    </FormDialog>
  );
}
